import { KnockoutMatch } from '../types/tournament';

export interface MatchPosition {
  x: number;
  y: number;
  match: KnockoutMatch;
}

export interface BracketLayout {
  width: number;
  height: number;
  matches: MatchPosition[];
}

// Match card actual dimensions (must match CSS)
const MATCH_WIDTH = 192; // w-48 = 12rem = 192px
const MATCH_HEIGHT = 100; // Approximate actual rendered height
const HORIZONTAL_GAP = 80;
const VERTICAL_GAP = 40;
const STAGGER_OFFSET = 60; // Horizontal stagger for alternating matches

// Match card dimensions for connector lines
export const CARD_WIDTH = MATCH_WIDTH;
export const CARD_HEIGHT = MATCH_HEIGHT;

export function calculateBracketLayout(matches: KnockoutMatch[]): BracketLayout {
  const positions: MatchPosition[] = [];

  // Separate matches by round
  const r32 = matches.filter(m => m.round === 'R32');
  const r16 = matches.filter(m => m.round === 'R16');
  const qf = matches.filter(m => m.round === 'QF');
  const sf = matches.filter(m => m.round === 'SF');
  const thirdPlace = matches.filter(m => m.round === '3RD');
  const final = matches.filter(m => m.round === 'F');

  // Layout with horizontal staggering
  // R32 matches alternate between two X positions so lines don't overlap

  const r32Spacing = MATCH_HEIGHT + VERTICAL_GAP;
  const r16Spacing = r32Spacing * 2;
  const qfSpacing = r16Spacing * 2;

  const totalHeight = 8 * r32Spacing;
  const centerY = totalHeight / 2 - MATCH_HEIGHT / 2;

  // X positions - R32 cards are progressively staggered, R16 alternates
  const R32_STEP = 30; // Each R32 card steps further right
  const r32MaxOffset = R32_STEP * 7; // Total offset for 8 R32 cards
  const baseX = MATCH_WIDTH + r32MaxOffset + HORIZONTAL_GAP;

  const col = {
    // Left side - R16 alternates between two positions
    leftR16_a: baseX,
    leftR16_b: baseX + STAGGER_OFFSET,
    leftQF: baseX + STAGGER_OFFSET + (MATCH_WIDTH + HORIZONTAL_GAP),
    leftSF: baseX + STAGGER_OFFSET + (MATCH_WIDTH + HORIZONTAL_GAP) * 2,
    center: baseX + STAGGER_OFFSET + (MATCH_WIDTH + HORIZONTAL_GAP) * 3,
    rightSF: baseX + STAGGER_OFFSET + (MATCH_WIDTH + HORIZONTAL_GAP) * 4,
    rightQF: baseX + STAGGER_OFFSET + (MATCH_WIDTH + HORIZONTAL_GAP) * 5,
    // Right side - R16 alternates (mirrored)
    rightR16_b: baseX + STAGGER_OFFSET + (MATCH_WIDTH + HORIZONTAL_GAP) * 6,
    rightR16_a: baseX + STAGGER_OFFSET + (MATCH_WIDTH + HORIZONTAL_GAP) * 6 + STAGGER_OFFSET,
    // Right side - R32 base position
    rightR32Base: baseX + STAGGER_OFFSET + (MATCH_WIDTH + HORIZONTAL_GAP) * 7 + STAGGER_OFFSET,
  };

  // Helper to get R32 X position (progressive stagger)
  const getLeftR32X = (index: number) => index * R32_STEP;
  const getRightR32X = (index: number) => col.rightR32Base + (7 - index) * R32_STEP;

  const totalWidth = getRightR32X(0) + MATCH_WIDTH;

  // Helper to get Y position
  const getR32Y = (index: number) => index * r32Spacing;
  const getR16Y = (index: number) => index * r16Spacing + r32Spacing / 2;
  const getQFY = (index: number) => index * qfSpacing + r16Spacing / 2 + r32Spacing / 2;

  // LEFT SIDE OF BRACKET
  // R32 - 8 matches, each progressively further right
  r32.slice(0, 8).forEach((match, i) => {
    positions.push({
      x: getLeftR32X(i),
      y: getR32Y(i),
      match,
    });
  });

  // R16 - 4 matches, alternating X positions
  r16.slice(0, 4).forEach((match, i) => {
    const isFirstInPair = i % 2 === 0;
    positions.push({
      x: isFirstInPair ? col.leftR16_a : col.leftR16_b,
      y: getR16Y(i),
      match,
    });
  });

  // QF - 2 matches on left (M97, M99)
  [qf[0], qf[2]].filter(Boolean).forEach((match, i) => {
    positions.push({
      x: col.leftQF,
      y: getQFY(i),
      match,
    });
  });

  // SF - 1 match on left (M101)
  if (sf[0]) {
    positions.push({
      x: col.leftSF,
      y: centerY,
      match: sf[0],
    });
  }

  // RIGHT SIDE OF BRACKET (mirror of left)
  // R32 - 8 matches, each progressively further right (mirrored)
  r32.slice(8, 16).forEach((match, i) => {
    positions.push({
      x: getRightR32X(i),
      y: getR32Y(i),
      match,
    });
  });

  // R16 - 4 matches on right, alternating X positions (mirrored)
  r16.slice(4, 8).forEach((match, i) => {
    const isFirstInPair = i % 2 === 0;
    positions.push({
      x: isFirstInPair ? col.rightR16_a : col.rightR16_b,
      y: getR16Y(i),
      match,
    });
  });

  // QF - 2 matches on right (M98, M100)
  [qf[1], qf[3]].filter(Boolean).forEach((match, i) => {
    positions.push({
      x: col.rightQF,
      y: getQFY(i),
      match,
    });
  });

  // SF - 1 match on right (M102)
  if (sf[1]) {
    positions.push({
      x: col.rightSF,
      y: centerY,
      match: sf[1],
    });
  }

  // CENTER - Final at center, 3rd Place offset below
  if (final[0]) {
    positions.push({
      x: col.center,
      y: centerY,
      match: final[0],
    });
  }

  if (thirdPlace[0]) {
    positions.push({
      x: col.center + HORIZONTAL_GAP,
      y: centerY + MATCH_HEIGHT + VERTICAL_GAP * 8,
      match: thirdPlace[0],
    });
  }

  return {
    width: totalWidth,
    height: totalHeight,
    matches: positions,
  };
}

export function getRoundLabel(round: string): string {
  const labels: Record<string, string> = {
    R32: 'Round of 32',
    R16: 'Round of 16',
    QF: 'Quarter-finals',
    SF: 'Semi-finals',
    '3RD': '3rd Place',
    F: 'Final',
  };
  return labels[round] || round;
}
